// src/AdminNotificationSettings.js
import React, { useState, useEffect } from 'react';
import { useAdmin } from './hooks/useAdmin';
import { useDancerAuth } from './hooks/useDancerAuth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { requestNotificationPermission, hasNotificationPermission, setupForegroundListener, getLastNotificationError } from './utils/notificationService';
import './AdminNotificationSettings.css';

export default function AdminNotificationSettings() {
  const { isAdmin } = useAdmin();
  const { user } = useDancerAuth();
  const [notificationStatus, setNotificationStatus] = useState('checking');
  const [fcmToken, setFcmToken] = useState(null);
  const [testNotification, setTestNotification] = useState('');
  const [widgetStatus, setWidgetStatus] = useState(null);

  // Rebuild the homepage "What's moving" widgets right away (they also refresh every 3 hours)
  const refreshWidgets = async () => {
    setWidgetStatus('Refreshing…');
    try {
      const res = await httpsCallable(getFunctions(), 'refreshHomepageWidgetsNow')();
      const d = res.data || {};
      setWidgetStatus(`✅ Updated: ${d.climbers || 0} climbers, ${d.newOnPodium || 0} new on podium, ${d.latestWinners || 0} latest winners`);
    } catch (e) {
      console.error(e);
      setWidgetStatus('❌ ' + (e.message || 'Refresh failed'));
    }
  };

  useEffect(() => {
    checkNotificationStatus();
    
    // Setup foreground message listener
    const setupListener = async () => {
      const unsubscribe = await setupForegroundListener((payload) => {
        console.log('📬 Notification received while app is open:', payload);
        setTestNotification(`Received: ${payload.notification?.title || 'Notification'}`);
        setTimeout(() => setTestNotification(''), 5000);
      });
      
      return () => {
        if (unsubscribe) unsubscribe();
      };
    };
    
    setupListener();
  }, []);

  const checkNotificationStatus = () => {
    if (!('Notification' in window)) {
      setNotificationStatus('not-supported');
      return;
    }

    const permission = Notification.permission;
    if (permission === 'granted') {
      setNotificationStatus('enabled');
    } else if (permission === 'denied') {
      setNotificationStatus('denied');
    } else {
      setNotificationStatus('not-enabled');
    }
  };

  const handleEnableNotifications = async () => {
    setNotificationStatus('requesting');
    
    const token = await requestNotificationPermission(user, isAdmin);
    
    if (token) {
      setFcmToken(token);
      setNotificationStatus('enabled');
      setTestNotification('✅ Notifications enabled successfully!');
      setTimeout(() => setTestNotification(''), 3000);
    } else {
      checkNotificationStatus();
      setTestNotification('❌ ' + (getLastNotificationError() || 'Could not enable notifications.'));
      setTimeout(() => setTestNotification(''), 10000);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-notification-settings">
      <div className="admin-notification-card">
        <h2>🔔 Push Notifications</h2>
        <p className="notification-description">
          Enable push notifications to receive instant alerts when dancers or studios submit profile claims.
        </p>

        <div className="notification-status">
          <h3>Status:</h3>
          {notificationStatus === 'checking' && (
            <div className="status-message status-checking">
              <span className="status-icon">⏳</span>
              <span>Checking notification settings...</span>
            </div>
          )}
          
          {notificationStatus === 'not-supported' && (
            <div className="status-message status-error">
              <span className="status-icon">❌</span>
              <span>Push notifications are not supported in this browser</span>
            </div>
          )}
          
          {notificationStatus === 'denied' && (
            <div className="status-message status-error">
              <span className="status-icon">🚫</span>
              <div>
                <p><strong>Notifications blocked</strong></p>
                <p className="status-help">
                  You've blocked notifications for this site. To enable:
                </p>
                <ol className="status-instructions">
                  <li>Click the lock icon in your browser's address bar</li>
                  <li>Find "Notifications" in the permissions list</li>
                  <li>Change it to "Allow"</li>
                  <li>Refresh this page</li>
                </ol>
              </div>
            </div>
          )}
          
          {notificationStatus === 'not-enabled' && (
            <div className="status-message status-warning">
              <span className="status-icon">⚠️</span>
              <span>Notifications are not enabled</span>
            </div>
          )}
          
          {notificationStatus === 'requesting' && (
            <div className="status-message status-checking">
              <span className="status-icon">⏳</span>
              <span>Requesting notification permission...</span>
            </div>
          )}
          
          {notificationStatus === 'enabled' && (
            <div className="status-message status-success">
              <span className="status-icon">✅</span>
              <span>Notifications are enabled</span>
            </div>
          )}
        </div>

        {notificationStatus === 'not-enabled' && (
          <button 
            className="btn-enable-notifications"
            onClick={handleEnableNotifications}
          >
            🔔 Enable Push Notifications
          </button>
        )}

        {notificationStatus === 'enabled' && (
          <div className="notification-info">
            <h3>📱 You will receive notifications for:</h3>
            <ul className="notification-types">
              <li>✅ New dancer profile claims</li>
              <li>✅ New studio profile claims</li>
            </ul>
            
            <div className="notification-help">
              <h4>📱 On Your Phone:</h4>
              <p>
                Notifications will appear even when the app is closed. 
                You can manage notification settings in your device's settings.
              </p>
            </div>
          </div>
        )}

        {testNotification && (
          <div className="test-notification">
            {testNotification}
          </div>
        )}

        {isAdmin && (
          <div className="test-notification" style={{ marginTop: '1.5rem' }}>
            <strong>🔥 Homepage widgets</strong>
            <p style={{ margin: '0.4rem 0' }}>
              Rebuilds “Biggest climbers / New on the podium / Latest winners” from recent ranking changes.
            </p>
            <button onClick={refreshWidgets}>Refresh widgets now</button>
            {widgetStatus && <p style={{ margin: '0.5rem 0 0' }}>{widgetStatus}</p>}
          </div>
        )}

        {fcmToken && (
          <details className="token-details">
            <summary>Technical Info (for debugging)</summary>
            <div className="token-info">
              <code>{fcmToken}</code>
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
